import * as React from 'react';
import { Col, Row } from 'reactstrap';
import ReactHtmlParser, { processNodes, convertNodeToElement } from 'react-html-parser';
import { IAgency } from "src/share/types/agency";
import { ICase } from "src/share/types/case/case";
import { momentToString } from 'src/share/utils/utils';
import logo from '../../../../../images/GMCNgine-Horizontal2x.png';
import { PosterPrintPerson } from './components/PosterPrintPerson';
import { PosterPrintRowPerson } from './components/PosterPrintRowPerson';
import { IChild } from 'src/share/types/case/children';
import { IAbductor } from 'src/share/types/case/abductor';
import './posterPrint.css'
import { getContactInformation } from 'src/share/utils/formatContactInfo';

export interface IPosterPrintProps {
    caseData: ICase;
    agency: IAgency;
    isCrossBorderPoster: boolean;
}

export interface IPosterPrintPersonProps {
    personInfo: IChild | IAbductor;
    className?: string;
    colSize?: number;
    colSizeInformation?: number;
}

const transform = (node, index) => {
    if (node.type === "tag" && node.name === "p") {
        return processNodes(node.children, transform)
    }
    return <span>{convertNodeToElement(node, index, transform)}</span>;
}

export default class PosterPrint extends React.Component<IPosterPrintProps, any> {
    constructor(props: IPosterPrintProps) {
        super(props);
        this.state = {
        }
    }

    public getPosterCaseInformation = () => {
        const { country, missingDate, lastSeen } = this.props.caseData;
        return { country, missingDate, lastSeen: lastSeen || 'Unknown' };
    }

    public getChildrenDistribution = () => {
        const { children } = this.props.caseData;
        const posterCaseInformation = this.getPosterCaseInformation();
        const posterOptions = ({
            1: (
                <div className={'poster-person-container'}>
                    <PosterPrintPerson personInfo={children[0]} {...posterCaseInformation} className={'one-child-poster'} colSize={12} />
                </div>
            ),
            2: (
                children.map(child => (
                    <div key={child.childId} className={'poster-person-container'}>
                        <PosterPrintRowPerson personInfo={child} {...posterCaseInformation} colSize={5} colSizeInformation={7} />
                    </div>
                ))
            ),
            3: (
                <>
                    <div className={'poster-person-container'}>
                        <PosterPrintPerson personInfo={children[0]} {...posterCaseInformation} className={'triple-children-poster'} colSize={6} />
                        <PosterPrintPerson personInfo={children[1]} {...posterCaseInformation} className={'triple-children-poster'} colSize={6} />
                    </div>
                    <div className={'poster-person-container'}>
                        <PosterPrintRowPerson personInfo={children[2]} {...posterCaseInformation} className={'triple-children-poster'} colSize={6} colSizeInformation={6} />
                    </div>
                </>
            ),
            4: (
                <>
                    <div className={'poster-person-container'}>
                        <PosterPrintPerson personInfo={children[0]} {...posterCaseInformation} colSize={6} />
                        <PosterPrintPerson personInfo={children[1]} {...posterCaseInformation} colSize={6} />
                    </div>
                    <div className={'poster-person-container'}>
                        <PosterPrintPerson personInfo={children[2]} {...posterCaseInformation} colSize={6} />
                        <PosterPrintPerson personInfo={children[3]} {...posterCaseInformation} colSize={6} />
                    </div>
                </>
            )
        })
        return posterOptions[children.length];
    }

    public getAbductorsDistribution() {
        const { abductors, miscellaneous } = this.props.caseData;
        const posterOptions = ({
            1: (
                <div className={'poster-person-container poster-abductor-container'}>
                    {miscellaneous && miscellaneous.length > 0 ?
                        <PosterPrintRowPerson personInfo={abductors[0]} {...this.getPosterCaseInformation()} colSize={4} colSizeInformation={8} className="poster-abductor-miscellaneous" /> :
                        <PosterPrintPerson personInfo={abductors[0]} {...this.getPosterCaseInformation()} colSize={12} />}
                </div>
            ),
            2: (
                abductors.map(abductor =>
                    <div key={abductor.abductorId} className={'poster-person-container poster-abductor-container'}>
                        <PosterPrintRowPerson personInfo={abductor} {...this.getPosterCaseInformation()} colSize={4} colSizeInformation={8} />
                    </div>)
            )
        });
        return posterOptions[abductors.length];
    }

    public render() {
        const { caseData: { abductors, circumstances, contactInformation, country, missingDate, miscellaneous }, agency, isCrossBorderPoster } = this.props;
        const childrenDistribution = this.getChildrenDistribution();
        const abductorsDistribution = this.getAbductorsDistribution();
        return (
            <Col className={'col-12 no-padding-left no-padding-right poster-print-col'}>
                <Row>
                    <Col className={'col-12 no-padding-left'}>
                        <div className={'poster-title-container'}>
                            <h1 className={'poster-title'}>{this.props.caseData.poster}</h1>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col className={'col-8 no-padding-left'}>
                        {childrenDistribution}
                    </Col>
                    <Col className={'col-4'}>
                        <div className={`poster-right-side-container ${abductors.length < 1 && 'poster-with-no-extra-content'} ${abductors.length > 1 && 'multiple-abductors'} subFont`}>
                            {abductors.length > 0 &&
                                <>
                                    <div className={'poster-abductor-title'}>
                                        <h1>Companion{abductors.length > 1 && 's'}</h1>
                                    </div>
                                    {abductorsDistribution}
                                </>}
                            <p><label>Missing Since:</label>{missingDate ? momentToString(missingDate) : 'Unknown'}</p>
                            <p><label>Country:</label>{country || 'Unknown'}</p>
                            {circumstances ? <><h2>Circumstances:</h2><p className="case-data-circumstances">{ReactHtmlParser(circumstances)}</p></> :
                                <p><label>Circumstances:</label>Unknown</p>}
                            {miscellaneous && miscellaneous.length > 0 &&
                                <div className="poster-miscellaneous-images">
                                    <h2>Miscellaneous</h2>
                                    {miscellaneous.map(image => (<img key={image} className={'child-image'} src={image} />))}
                                </div>}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <div className={'poster-bottom-bar poster-agency-info-container'}>
                        <div className="agency-logo-background">
                            <img src={agency && agency.logo && agency.logo.url} alt="Logo" />
                        </div>
                        <div className="agency-contact-info">
                            <div className="information-container">
                                <p className="information">
                                    {agency && agency.contact ? <><span className="title">Contact Info:</span>
                                        {ReactHtmlParser(getContactInformation(isCrossBorderPoster ? agency.contact : contactInformation), { transform })}</> : 'Unknown'}
                                </p>
                            </div>
                        </div>
                        <div className="powered-by-gmcngine">
                            <span>powered by:</span>
                            <img src={logo} alt="ICMEC" className="nav-bar-logo" />
                        </div>
                    </div>
                </Row>
            </Col>
        );
    }
}
